(function () {
  'use strict';

  if (window.PellairEnhancements && window.PellairEnhancements.version === 'v31-error-checked') return;

  const theme = window.PellairTheme && typeof window.PellairTheme === 'object' ? window.PellairTheme : {};
  const routes = theme.routes || {};
  const assets = theme.assets || {};
  const predictiveControllers = new WeakMap();
  const filterControllers = new WeakMap();
  const activeFilterDrawers = new Set();
  const dataCache = new Map();
  const indexMaps = new WeakMap();

  function normalize(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function escapeHTML(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, function (character) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[character];
    });
  }

  function safeHref(value, fallback) {
    try {
      const url = new URL(String(value || ''), window.location.href);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return fallback || '#';
      return url.origin === window.location.origin ? url.pathname + url.search + url.hash : url.href;
    } catch (error) {
      return fallback || '#';
    }
  }

  function safeImage(value) {
    try {
      const url = new URL(String(value || ''), window.location.href);
      return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : '';
    } catch (error) {
      return '';
    }
  }

  function isPrivateServiceProduct(product) {
    if (!product || typeof product !== 'object') return false;

    const handle = String(product.handle || product.shopifyHandle || '').trim().toLowerCase();
    if (handle === 'finished-perfume-preparation') return true;

    const productUrl = product.url || product.productUrl || '';
    if (productUrl) {
      try {
        const pathname = new URL(String(productUrl), window.location.origin).pathname
          .replace(/\/+$/, '')
          .toLowerCase();
        if (pathname.endsWith('/products/finished-perfume-preparation')) return true;
      } catch (error) {
        // An invalid product URL is handled by the normal safe-link fallback below.
      }
    }

    const tags = Array.isArray(product.tags)
      ? product.tags
      : String(product.tags || '').split(',');
    return tags.some(function (tag) {
      return String(tag || '').trim().toLowerCase() === 'hidden-service';
    });
  }

  function fetchData(url, signal) {
    if (!url) return Promise.reject(new Error('Missing data URL.'));
    if (!dataCache.has(url)) {
      dataCache.set(url, window.fetch(url, {
        credentials: 'same-origin',
        cache: 'force-cache',
        headers: { Accept: 'application/json' },
        signal: signal
      }).then(function (response) {
        if (!response.ok) throw new Error('The data request failed.');
        return response.json();
      }).catch(function (error) {
        dataCache.delete(url);
        throw error;
      }));
    }
    return dataCache.get(url);
  }

  function buildIndex(data) {
    if (indexMaps.has(data)) return indexMaps.get(data);
    const byHandle = new Map();
    const byTitle = new Map();
    (Array.isArray(data && data.products) ? data.products : []).forEach(function (product) {
      if (isPrivateServiceProduct(product)) return;
      const handle = normalize(product && (product.handle || product.shopifyHandle));
      const title = normalize(product && (product.title || product.shopifyTitle || [product.house, product.fragrance].filter(Boolean).join(' - ')));
      if (handle && !byHandle.has(handle)) byHandle.set(handle, product);
      if (title) {
        if (!byTitle.has(title)) byTitle.set(title, product);
        else byTitle.set(title, null);
      }
    });
    byTitle.forEach(function (product, title) {
      if (!product) byTitle.delete(title);
    });
    const index = { byHandle: byHandle, byTitle: byTitle };
    indexMaps.set(data, index);
    return index;
  }

  function findRecord(data, handle, title) {
    const index = buildIndex(data);
    const handleKey = normalize(handle);
    const titleKey = normalize(title);
    return index.byHandle.get(handleKey) || index.byTitle.get(titleKey) || null;
  }

  function schedule(callback, timeout) {
    if ('requestIdleCallback' in window) return window.requestIdleCallback(callback, { timeout: timeout || 900 });
    return window.setTimeout(callback, Math.min(timeout || 240, 320));
  }

  function createPredictiveSearch(details) {
    if (!details || predictiveControllers.has(details)) return;
    const panel = details.querySelector('[data-predictive-panel]');
    const form = details.querySelector('[data-predictive-form]');
    const input = details.querySelector('[data-predictive-input]');
    const results = details.querySelector('[data-predictive-results]');
    const listbox = details.querySelector('[data-predictive-listbox]');
    const message = details.querySelector('[data-predictive-message]');
    const status = details.querySelector('[data-predictive-status]');
    const clear = details.querySelector('[data-predictive-clear]');
    const close = details.querySelector('[data-predictive-close]');
    const scrim = details.querySelector('[data-predictive-scrim]');
    const summary = details.querySelector('summary');
    const endpoint = details.getAttribute('data-predictive-url') || routes.predictiveSearch || '/search/suggest.json';
    if (!panel || !form || !input || !results || !listbox || !message || !status) return;
    let timer = 0;
    let request = null;
    let activeIndex = -1;
    let requestSequence = 0;
    let destroyed = false;
    const removers = [];

    function listen(target, type, handler, options) {
      if (!target) return;
      target.addEventListener(type, handler, options);
      removers.push(function () { target.removeEventListener(type, handler, options); });
    }

    function links() {
      return Array.from(listbox.querySelectorAll('[data-predictive-option]'));
    }

    function setStatus(message, state) {
      status.textContent = message || '';
      status.dataset.state = state || 'idle';
      results.dataset.state = state || 'idle';
      listbox.setAttribute('aria-busy', String(state === 'loading'));
    }

    function setExpanded(expanded) {
      input.setAttribute('aria-expanded', String(expanded));
    }

    function setSearchOpen(open) {
      const isOpen = !!open;
      document.documentElement.classList.toggle('predictive-search-open', isOpen);
      document.body.classList.toggle('predictive-search-open', isOpen);
      if (summary) {
        summary.setAttribute('aria-expanded', String(isOpen));
        summary.setAttribute('aria-label', isOpen ? 'Close search' : 'Open search');
      }
      if (isOpen) return;
      window.clearTimeout(timer);
      timer = 0;
      requestSequence += 1;
      if (request) request.abort();
      request = null;
      activeIndex = -1;
      setExpanded(false);
      setStatus('', 'idle');
      listbox.innerHTML = '';
      listbox.hidden = true;
      message.innerHTML = '';
    }

    function closeSearch(restoreFocus) {
      details.open = false;
      setSearchOpen(false);
      if (restoreFocus && summary) summary.focus({ preventScroll: true });
    }

    function focusableElements() {
      return Array.from(panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])')).filter(function (element) {
        return !element.hidden && element.getAttribute('aria-hidden') !== 'true' && element.offsetParent !== null;
      });
    }

    function highlight(nextIndex) {
      const items = links();
      if (!items.length) return;
      activeIndex = Math.max(0, Math.min(items.length - 1, nextIndex));
      items.forEach(function (item, index) {
        item.classList.toggle('is-active', index === activeIndex);
        item.setAttribute('aria-selected', String(index === activeIndex));
      });
      items[activeIndex].focus({ preventScroll: true });
    }

    function regularSearchURL(query) {
      const url = new URL(routes.search || '/search', window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('options[prefix]', 'last');
      return safeHref(url.href, (routes.search || '/search') + '?q=' + encodeURIComponent(query));
    }

    function renderLoading() {
      listbox.innerHTML = '';
      listbox.hidden = true;
      message.innerHTML = '<div class="pellair-search-panel__loading" aria-hidden="true"><span></span><span></span><span></span></div>';
      setExpanded(false);
      setStatus('Searching the Pellair Oils archive.', 'loading');
    }

    function renderProducts(products, query) {
      products = products.filter(function (product) {
        return !isPrivateServiceProduct(product);
      });
      if (!products.length) {
        listbox.innerHTML = '';
        listbox.hidden = true;
        message.innerHTML = '<div class="pellair-search-panel__empty"><p>No oils found for &ldquo;' + escapeHTML(query) + '&rdquo;.</p><span>Try a broader name, explore a scent filter below, or browse the complete archive.</span><a href="' + escapeHTML(routes.allProducts || '/collections/all') + '">Browse all oils</a></div>';
        setStatus('No matching oils found. Scent filters and the complete archive remain available.', 'empty');
        setExpanded(false);
        return;
      }
      listbox.hidden = false;
      listbox.innerHTML = products.map(function (product) {
        const imageSource = product.featured_image && (product.featured_image.url || product.featured_image) || product.image;
        const image = safeImage(imageSource);
        const href = safeHref(product.url, routes.search || '/search');
        const title = String(product.title || 'Pellair oil');
        const vendor = String(product.vendor || '').trim();
        const price = String(product.price || '').trim();
        return '<a class="pellair-search-panel__item" role="option" aria-selected="false" data-predictive-option href="' + escapeHTML(href) + '">' +
          (image ? '<img src="' + escapeHTML(image) + '" alt="" width="72" height="88" loading="lazy">' : '<span class="pellair-search-panel__placeholder" aria-hidden="true">PO</span>') +
          '<span class="pellair-search-panel__meta">' + (vendor ? '<span class="pellair-search-panel__vendor">' + escapeHTML(vendor) + '</span>' : '') + '<strong>' + escapeHTML(title) + '</strong>' +
          (price ? '<span class="pellair-search-panel__price">' + escapeHTML(price) + '</span>' : '') + '</span></a>';
      }).join('');
      message.innerHTML = '<a class="pellair-search-panel__all" href="' + escapeHTML(regularSearchURL(query)) + '">View all results for &ldquo;' + escapeHTML(query) + '&rdquo;</a>';
      setStatus(products.length + ' product suggestion' + (products.length === 1 ? '' : 's') + '.', 'ready');
      setExpanded(true);
      activeIndex = -1;
    }

    function search(query) {
      if (request) request.abort();
      const sequence = ++requestSequence;
      request = new AbortController();
      renderLoading();
      const url = new URL(endpoint, window.location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      window.fetch(url.pathname + url.search, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal: request.signal
      }).then(function (response) {
        if (!response.ok) throw new Error('Search failed.');
        return response.json();
      }).then(function (data) {
        if (destroyed || sequence !== requestSequence || input.value.trim() !== query) return;
        const products = data && data.resources && data.resources.results && data.resources.results.products || [];
        renderProducts(Array.isArray(products) ? products : [], query);
      }).catch(function (error) {
        if (error && error.name === 'AbortError' || destroyed || sequence !== requestSequence) return;
        listbox.innerHTML = '';
        listbox.hidden = true;
        message.innerHTML = '<div class="pellair-search-panel__empty"><p>Live suggestions are temporarily unavailable.</p><span>Your regular Shopify search still works.</span><button class="plain" type="submit" form="' + escapeHTML(form.id || '') + '">Search normally</button></div>';
        setExpanded(false);
        setStatus('Live suggestions are unavailable. Submit the search to continue.', 'error');
      }).finally(function () {
        if (sequence === requestSequence) request = null;
      });
    }

    function onInput() {
      const query = input.value.trim();
      if (clear) clear.hidden = !query;
      window.clearTimeout(timer);
      timer = 0;
      requestSequence += 1;
      if (request) request.abort();
      request = null;
      activeIndex = -1;
      listbox.innerHTML = '';
      listbox.hidden = true;
      message.innerHTML = '';
      if (query.length < 2) {
        setStatus(query ? 'Enter one more character for suggestions.' : '', 'idle');
        setExpanded(false);
        return;
      }
      setStatus('Preparing suggestions.', 'pending');
      setExpanded(false);
      timer = window.setTimeout(function () { search(query); }, 180);
    }

    function onToggle() {
      if (details.open) {
        setSearchOpen(true);
        window.requestAnimationFrame(function () { input.focus({ preventScroll: true }); });
        onInput();
      } else {
        setSearchOpen(false);
      }
    }

    function onInputKeydown(event) {
      if (event.key === 'ArrowDown' && links().length) {
        event.preventDefault();
        highlight(0);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch(true);
      }
    }

    function onResultsKeydown(event) {
      const items = links();
      if (!items.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        highlight((activeIndex < 0 ? 0 : activeIndex + 1));
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (activeIndex <= 0) {
          activeIndex = -1;
          items.forEach(function (item) { item.classList.remove('is-active'); item.setAttribute('aria-selected', 'false'); });
          input.focus({ preventScroll: true });
        } else highlight(activeIndex - 1);
      } else if (event.key === 'Home') {
        event.preventDefault();
        highlight(0);
      } else if (event.key === 'End') {
        event.preventDefault();
        highlight(items.length - 1);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        closeSearch(true);
      }
    }

    function onDocumentKeydown(event) {
      if (!details.open || event.key !== 'Tab') return;
      const focusable = focusableElements();
      if (!focusable.length) {
        event.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    function onClear() {
      input.value = '';
      onInput();
      input.focus({ preventScroll: true });
    }

    function onSubmit(event) {
      if (input.value.trim()) return;
      event.preventDefault();
      setStatus('Enter an oil or fragrance house to search.', 'error');
      input.focus({ preventScroll: true });
    }

    listen(details, 'toggle', onToggle);
    listen(input, 'input', onInput);
    listen(input, 'keydown', onInputKeydown);
    listen(listbox, 'keydown', onResultsKeydown);
    listen(document, 'keydown', onDocumentKeydown);
    listen(form, 'submit', onSubmit);
    if (clear) listen(clear, 'click', onClear);
    if (close) listen(close, 'click', function () { closeSearch(true); });
    if (scrim) listen(scrim, 'click', function () { closeSearch(true); });
    listbox.hidden = true;
    setSearchOpen(details.open);

    predictiveControllers.set(details, {
      pageHide: function () { closeSearch(false); },
      destroy: function () {
        destroyed = true;
        closeSearch(false);
        removers.splice(0).forEach(function (remove) { remove(); });
      }
    });
  }

  function createFilterDrawer(drawer) {
    if (!drawer || filterControllers.has(drawer)) return;
    const form = drawer.closest('[data-collection-filter-form]');
    const trigger = form && form.querySelector('[data-collection-filter-open]');
    const panel = drawer.querySelector('[data-collection-filter-drawer-panel]');
    const closeButtons = Array.from(drawer.querySelectorAll('[data-collection-filter-close]'));
    const selectedTargets = form ? Array.from(form.querySelectorAll('[data-filter-selected-count]')) : [];
    const selectedSummary = drawer.querySelector('[data-filter-selected-summary]');
    const applyButton = form && form.querySelector('[data-collection-filter-apply]');
    const applyLabel = form && form.querySelector('[data-filter-apply-label]');
    const initialFocus = panel && (panel.querySelector('.collection-filter-drawer__close') || panel);
    const productGrid = document.querySelector('[data-product-grid]');
    const mobile = window.matchMedia ? window.matchMedia('(max-width: 749px)') : { matches: false };
    if (!form || !trigger || !panel) return;
    let open = false;
    let destroyed = false;
    let dirty = false;
    let focusFrame = 0;
    let focusTimer = 0;
    const initialApplyLabel = applyLabel ? applyLabel.textContent.trim() : '';

    function groupSelectionCount(group) {
      if (!group) return 0;
      if (group.getAttribute('data-filter-type') === 'price_range') {
        return Array.from(group.querySelectorAll('[data-filter-input]')).some(function (input) {
          return String(input.value || '').trim() !== '';
        }) ? 1 : 0;
      }
      return group.querySelectorAll('[data-filter-input]:checked').length;
    }

    function syncSelections(markDirty) {
      if (markDirty) dirty = true;
      let count = 0;
      drawer.querySelectorAll('[data-filter-group]').forEach(function (group) {
        const groupCount = groupSelectionCount(group);
        const badge = group.querySelector('[data-filter-group-count]');
        const summary = group.querySelector('[data-filter-group-summary]');
        count += groupCount;
        if (badge) {
          badge.textContent = String(groupCount);
          badge.hidden = groupCount === 0;
          badge.setAttribute('aria-label', groupCount + ' selected');
        }
        if (summary) summary.textContent = groupCount ? groupCount + ' selected' : 'Choose any';
      });

      selectedTargets.forEach(function (target) {
        target.textContent = String(count);
        target.setAttribute('aria-label', count + ' selected');
      });
      if (selectedSummary) {
        selectedSummary.textContent = count
          ? count + (count === 1 ? ' filter selected' : ' filters selected')
          : 'No filters selected';
      }
      if (applyLabel) {
        applyLabel.textContent = dirty
          ? (count ? 'Apply ' + count + (count === 1 ? ' filter' : ' filters') : 'View all oils')
          : initialApplyLabel;
      }
      return count;
    }

    function focusable() {
      return Array.from(panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),summary,[tabindex]:not([tabindex="-1"])')).filter(function (element) {
        return !element.hidden && element.offsetParent !== null;
      });
    }

    function releasePageLock() {
      document.documentElement.classList.remove('collection-filter-open');
      document.body.classList.remove('collection-filter-open');
    }

    function clearDialogSemantics() {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      panel.removeAttribute('aria-labelledby');
    }

    function syncMode() {
      if (destroyed) return;
      if (!mobile.matches) {
        open = false;
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'false');
        clearDialogSemantics();
        trigger.setAttribute('aria-expanded', 'false');
        releasePageLock();
      } else if (!open) {
        drawer.setAttribute('aria-hidden', 'true');
        clearDialogSemantics();
      }
    }

    function setOpen(next, restoreFocus) {
      if (destroyed || !mobile.matches) return;
      open = !!next;
      drawer.classList.toggle('is-open', open);
      drawer.setAttribute('aria-hidden', String(!open));
      trigger.setAttribute('aria-expanded', String(open));
      document.documentElement.classList.toggle('collection-filter-open', open);
      document.body.classList.toggle('collection-filter-open', open);
      if (open) {
        panel.setAttribute('role', 'dialog');
        panel.setAttribute('aria-modal', 'true');
        const title = panel.querySelector('h2[id]');
        if (title) panel.setAttribute('aria-labelledby', title.id);
        initialFocus.focus({ preventScroll: true });
        window.cancelAnimationFrame(focusFrame);
        window.clearTimeout(focusTimer);
        focusFrame = window.requestAnimationFrame(function () {
          focusTimer = window.setTimeout(function () {
            if (open && !destroyed && !panel.contains(document.activeElement)) {
              initialFocus.focus({ preventScroll: true });
            }
          }, 280);
        });
      } else {
        window.cancelAnimationFrame(focusFrame);
        window.clearTimeout(focusTimer);
        clearDialogSemantics();
        if (restoreFocus) trigger.focus({ preventScroll: true });
      }
    }

    function onKeydown(event) {
      if (!open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        setOpen(false, true);
        return;
      }
      if (event.key !== 'Tab') return;
      const items = focusable();
      if (!items.length) {
        event.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      } else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    function validatePrices() {
      let valid = true;
      drawer.querySelectorAll('[data-filter-type="price_range"]').forEach(function (group) {
        const inputs = Array.from(group.querySelectorAll('[data-filter-input]'));
        const minimum = inputs[0];
        const maximum = inputs[1];
        if (!minimum || !maximum) return;
        minimum.setCustomValidity('');
        maximum.setCustomValidity('');
        const minValue = String(minimum.value || '').trim() === '' ? null : Number(minimum.value);
        const maxValue = String(maximum.value || '').trim() === '' ? null : Number(maximum.value);
        if (minValue != null && maxValue != null && minValue > maxValue) {
          maximum.setCustomValidity('Maximum price must be greater than or equal to minimum price.');
          group.open = true;
          valid = false;
        }
      });
      return valid;
    }

    function onTriggerClick() { setOpen(true, false); }
    function onCloseClick() { setOpen(false, true); }
    function onFilterInput(event) {
      if (!event.target.matches('[data-filter-input]')) return;
      event.target.setCustomValidity('');
      syncSelections(true);
    }
    function onSubmit(event) {
      if (!validatePrices()) {
        event.preventDefault();
        const invalid = form.querySelector(':invalid');
        if (invalid) invalid.reportValidity();
        return;
      }
      form.setAttribute('aria-busy', 'true');
      if (productGrid) productGrid.setAttribute('aria-busy', 'true');
      if (applyButton) applyButton.disabled = true;
      setOpen(false, false);
    }
    function onMediaChange() { syncMode(); }

    trigger.addEventListener('click', onTriggerClick);
    closeButtons.forEach(function (button) { button.addEventListener('click', onCloseClick); });
    document.addEventListener('keydown', onKeydown, true);
    drawer.addEventListener('input', onFilterInput);
    drawer.addEventListener('change', onFilterInput);
    form.addEventListener('submit', onSubmit);
    if (mobile.addEventListener) mobile.addEventListener('change', onMediaChange);
    else if (mobile.addListener) mobile.addListener(onMediaChange);

    selectedSummary && selectedSummary.setAttribute('aria-live', 'polite');
    selectedSummary && selectedSummary.setAttribute('aria-atomic', 'true');
    form.classList.add('is-filter-enhanced');
    drawer.classList.add('is-enhanced');
    syncSelections(false);
    syncMode();

    const controller = {
      close: function () { setOpen(false, false); },
      pageHide: function () {
        setOpen(false, false);
        releasePageLock();
      },
      pageShow: function () {
        dirty = false;
        form.setAttribute('aria-busy', 'false');
        if (productGrid) productGrid.setAttribute('aria-busy', 'false');
        if (applyButton) applyButton.disabled = false;
        setOpen(false, false);
        syncSelections(false);
        syncMode();
      },
      destroy: function () {
        if (destroyed) return;
        destroyed = true;
        window.cancelAnimationFrame(focusFrame);
        window.clearTimeout(focusTimer);
        trigger.removeEventListener('click', onTriggerClick);
        closeButtons.forEach(function (button) { button.removeEventListener('click', onCloseClick); });
        document.removeEventListener('keydown', onKeydown, true);
        drawer.removeEventListener('input', onFilterInput);
        drawer.removeEventListener('change', onFilterInput);
        form.removeEventListener('submit', onSubmit);
        if (mobile.removeEventListener) mobile.removeEventListener('change', onMediaChange);
        else if (mobile.removeListener) mobile.removeListener(onMediaChange);
        drawer.classList.remove('is-open', 'is-enhanced');
        drawer.removeAttribute('aria-hidden');
        form.classList.remove('is-filter-enhanced');
        trigger.setAttribute('aria-expanded', 'false');
        clearDialogSemantics();
        releasePageLock();
        activeFilterDrawers.delete(drawer);
        filterControllers.delete(drawer);
      }
    };
    filterControllers.set(drawer, controller);
    activeFilterDrawers.add(drawer);
  }

  const profilePlaceholderParts = [
    ['raw', 'aroma', 'chem'].join(''),
    ['raw', 'aroma', 'chem'].join(' '),
    ['same', 'direction'].join(' '),
    ['brand', 'direction'].join(' '),
    ['needs', 'direct', 'page', 'capture'].join(' '),
    ['source', 'unavailable'].join(' ')
  ];

  function cleanProfileText(value, maximumLength) {
    const text = String(value == null ? '' : value)
      .replace(/<[^>]*>/g, ' ')
      .replace(/[\u0000-\u001f\u007f]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, maximumLength || 320);
    const key = normalize(text);
    if (!text || profilePlaceholderParts.some(function (marker) { return key.indexOf(marker) !== -1; })) return '';
    return text;
  }

  function usableProfileNote(value) {
    const key = normalize(value);
    if (!key) return false;
    return ![
      'no exact perfume page found',
      'page has no note pyramid',
      'official mfk amyris material described',
      'original fahrenheit page',
      'appears to keep good girl profile'
    ].some(function (marker) {
      return key.indexOf(marker) !== -1;
    });
  }

  function chipList(container, values, className, limit) {
    if (!container) return false;
    const seen = new Set();
    Array.from(container.children).forEach(function (child) {
      const key = normalize(child.textContent);
      if (key) seen.add(key);
    });
    if (!Array.isArray(values) || !values.length) return container.children.length > 0;
    values.forEach(function (value) {
      if (container.children.length >= (limit || 12)) return;
      const text = cleanProfileText(value, 80);
      const key = normalize(text);
      if (!text || !key || seen.has(key)) return;
      seen.add(key);
      const chip = document.createElement('span');
      chip.className = className || 'fragrance-chip';
      chip.textContent = text;
      container.appendChild(chip);
    });
    container.hidden = false;
    return container.children.length > 0;
  }

  function hydrateProfile(panel, record) {
    if (!panel || !record) return;
    let populated = !panel.hidden;
    const recordNotes = Array.isArray(record.notes) ? record.notes.filter(usableProfileNote) : [];
    const groups = panel.querySelector('[data-fragrance-groups]');
    const accordContainer = panel.querySelector('[data-fragrance-accords]');
    if (chipList(accordContainer, record.accords, 'fragrance-chip fragrance-chip--accord', 999)) {
      const group = panel.querySelector('[data-fragrance-accords-group]');
      if (group) group.hidden = false;
      if (groups) groups.hidden = false;
      populated = true;
    }

    const notesContainer = panel.querySelector('[data-fragrance-notes]');
    if (chipList(notesContainer, recordNotes, 'fragrance-chip', 999)) {
      const group = panel.querySelector('[data-fragrance-notes-group]');
      if (group) group.hidden = false;
      populated = true;
    }

    const pyramid = panel.querySelector('[data-fragrance-pyramid]');
    if (pyramid) {
      const levels = Array.isArray(record.noteLevels) ? record.noteLevels.filter(Boolean).slice() : [];
      const usedNotes = new Set();
      const displayLevelLabel = function (value) {
        const key = normalize(value);
        if (/^(?:top|top notes?|opening)$/.test(key)) return 'Top';
        if (/^(?:middle|middle notes?|heart|heart notes?)$/.test(key)) return 'Heart';
        if (/^(?:base|base notes?|dry down|drydown)$/.test(key)) return 'Base';
        return 'Notes';
      };
      const hasStructuredLevels = levels.some(function (level) {
        return displayLevelLabel(level.label) !== 'Notes';
      });
      const noteValues = function (level) {
        const values = Array.isArray(level.notes)
          ? level.notes
          : String(level.text || '').split(/\s*(?:,|;|\u2022|\u00b7)\s*/);
        return values.map(function (value) {
          return cleanProfileText(value, 160);
        }).filter(usableProfileNote);
      };
      levels.forEach(function (level) {
        noteValues(level).forEach(function (value) {
          const key = normalize(value);
          if (key) usedNotes.add(key);
        });
      });
      const remainingNotes = recordNotes.filter(function (note) {
        const key = normalize(note);
        return key && !usedNotes.has(key);
      });
      if (!levels.length && recordNotes.length) {
        levels.push({ label: 'Notes', notes: recordNotes, text: recordNotes.join(', ') });
      } else if (remainingNotes.length) {
        levels.push({ label: 'Notes', notes: remainingNotes, text: remainingNotes.join(', ') });
      }

      if (hasStructuredLevels) {
        Array.from(pyramid.querySelectorAll('.fragrance-pyramid__row')).forEach(function (candidate) {
          const labelElement = candidate.firstElementChild;
          if (labelElement && displayLevelLabel(labelElement.textContent) === 'Notes') candidate.remove();
        });
      }

      levels.forEach(function (level) {
        const rawLabel = String(level.label || 'Notes').trim();
        const displayLabel = displayLevelLabel(rawLabel);
        const values = noteValues(level);
        const existingRow = Array.from(pyramid.querySelectorAll('.fragrance-pyramid__row')).find(function (candidate) {
          const labelElement = candidate.firstElementChild;
          return labelElement && displayLevelLabel(labelElement.textContent) === displayLabel;
        });
        if (existingRow) {
          const existingText = existingRow.querySelector('[data-note-level-text]') || existingRow.querySelector('p');
          if (existingText && values.length) {
            const combined = [];
            const seen = new Set();
            String(existingText.textContent || '').split(/\s*(?:,|;|\u2022|\u00b7)\s*/).concat(values).forEach(function (value) {
              const cleaned = cleanProfileText(value, 160);
              const key = normalize(cleaned);
              if (!cleaned || !key || seen.has(key)) return;
              seen.add(key);
              combined.push(cleaned);
            });
            existingText.setAttribute('data-note-level-text', '');
            existingText.textContent = combined.join(', ');
          }
          return;
        }

        const row = document.createElement('div');
        const label = document.createElement('span');
        const body = document.createElement('div');
        const text = document.createElement('p');
        const visuals = document.createElement('div');
        row.className = 'fragrance-pyramid__row';
        row.dataset.noteStage = normalize(displayLabel) || 'notes';
        body.className = 'fragrance-pyramid__body';
        label.setAttribute('role', 'heading');
        label.setAttribute('aria-level', '3');
        visuals.className = 'fragrance-pyramid__visuals';
        visuals.setAttribute('data-note-level-visuals', '');
        visuals.setAttribute('role', 'group');
        visuals.setAttribute('aria-label', displayLabel === 'Notes' ? 'Fragrance notes' : displayLabel + ' notes');
        text.setAttribute('data-note-level-text', '');
        label.textContent = displayLabel === 'Notes' ? 'Key notes' : displayLabel;
        text.textContent = cleanProfileText(values.join(', '), 1200);
        if (text.textContent) {
          body.append(text, visuals);
          row.append(label, body);
          pyramid.appendChild(row);
        }
      });
      if (pyramid.querySelector('.fragrance-pyramid__row')) {
        pyramid.hidden = false;
        const allNotes = panel.querySelector('[data-fragrance-notes-group]');
        if (allNotes && allNotes.tagName === 'DETAILS') allNotes.removeAttribute('open');
        populated = true;
      }
    }

    if (populated) panel.hidden = false;
    panel.dataset.fragranceHydrated = 'true';
  }

  function hydrateCard(card, record) {
    if (!card || !record || card.dataset.fragranceHydrated === 'true') return;
    const terms = Array.isArray(record.accords) && record.accords.length ? record.accords : record.notes;
    const chips = card.querySelector('[data-fragrance-chips]');
    if (chipList(chips, terms, 'product-card__note', 2)) {
      chips.hidden = false;
    }
    card.dataset.fragranceHydrated = 'true';
  }

  function hydrateProfiles(root) {
    const panels = Array.from((root || document).querySelectorAll('[data-fragrance-detail][data-profile-needs-fallback="true"]:not([data-fragrance-hydrated])'));
    panels.forEach(function (panel) {
      const url = panel.getAttribute('data-profile-index-url') || assets.scentProfileIndex;
      if (!url || panel.dataset.fragranceLoading === 'true') return;
      panel.dataset.fragranceLoading = 'true';
      schedule(function () {
        fetchData(url).then(function (data) {
          const record = findRecord(data, panel.getAttribute('data-fragrance-handle'), panel.getAttribute('data-fragrance-title'));
          if (record) hydrateProfile(panel, record);
          else panel.dataset.fragranceHydrated = 'unavailable';
        }).catch(function () {
          panel.dataset.fragranceHydrated = 'unavailable';
        }).finally(function () {
          delete panel.dataset.fragranceLoading;
        });
      }, 700);
    });
  }

  function hydrateCards(root) {
    const cards = Array.from((root || document).querySelectorAll('[data-fragrance-card]:not([data-fragrance-hydrated="true"])'));
    const needed = cards.filter(function (card) {
      const description = card.querySelector('[data-fragrance-description]');
      const chips = card.querySelector('[data-fragrance-chips]');
      return description && !description.textContent.trim() || chips && !chips.children.length;
    });
    if (!needed.length || !assets.finderIndex) return;
    schedule(function () {
      fetchData(assets.finderIndex).then(function (data) {
        needed.forEach(function (card) {
          hydrateCard(card, findRecord(data, card.getAttribute('data-fragrance-handle'), card.getAttribute('data-fragrance-title')));
        });
      }).catch(function () {});
    }, 1200);
  }

  function init(root) {
    const scope = root && root.querySelectorAll ? root : document;
    if (scope.matches && scope.matches('[data-predictive-search]')) createPredictiveSearch(scope);
    scope.querySelectorAll('[data-predictive-search]').forEach(createPredictiveSearch);
    if (scope.matches && scope.matches('[data-collection-filter-drawer]')) createFilterDrawer(scope);
    scope.querySelectorAll('[data-collection-filter-drawer]').forEach(createFilterDrawer);
    hydrateProfiles(scope);
    hydrateCards(scope);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); }, { once: true });
  else init(document);

  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('[data-predictive-search]').forEach(function (element) {
      const controller = predictiveControllers.get(element);
      if (controller) controller.destroy();
      predictiveControllers.delete(element);
    });
    const filterDrawers = [];
    if (event.target.matches && event.target.matches('[data-collection-filter-drawer]')) filterDrawers.push(event.target);
    filterDrawers.push.apply(filterDrawers, event.target.querySelectorAll('[data-collection-filter-drawer]'));
    filterDrawers.forEach(function (element) {
      const controller = filterControllers.get(element);
      if (controller) controller.destroy();
    });
  });
  window.addEventListener('pagehide', function () {
    document.querySelectorAll('[data-predictive-search]').forEach(function (element) {
      const controller = predictiveControllers.get(element);
      if (controller && controller.pageHide) controller.pageHide();
    });
    activeFilterDrawers.forEach(function (element) {
      const controller = filterControllers.get(element);
      if (controller) controller.pageHide();
    });
    document.documentElement.classList.remove('collection-filter-open');
    document.body.classList.remove('collection-filter-open');
  });
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    activeFilterDrawers.forEach(function (element) {
      const controller = filterControllers.get(element);
      if (controller) controller.pageShow();
    });
    init(document);
  });

  window.PellairEnhancements = { version: 'v31-error-checked', init: init };
})();
